import { useState, useEffect } from "react";
import { Shield } from "lucide-react";
import { Button } from "@/components/ui/button";

const COOKIE_CONSENT_KEY = "adbit_cookie_consent";

export function getCookieConsent() {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
}

export function saveCookieConsent(consent) {
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() }));
  // Dispatch event so other components can react
  window.dispatchEvent(new CustomEvent("cookie-consent-updated", { detail: consent }));
}

export function loadAnalytics() {
  const consent = getCookieConsent();
  if (!consent?.analytics) return;
  // Google Analytics — load only after consent
  // Replace GA_MEASUREMENT_ID with your actual ID when ready
  if (!window.gtag && consent.analytics) {
    const script = document.createElement("script");
    script.src = "https://www.googletagmanager.com/gtag/js?id=G-C1MJ333LKE";
    script.async = true;
    document.head.appendChild(script);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", "G-C1MJ333LKE");
  }
}

export function loadAdvertising() {
  const consent = getCookieConsent();
  if (!consent?.advertising) return;
  // Meta Pixel
  if (!window.fbq && consent.advertising) {
    const script = document.createElement("script");
    script.innerHTML = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','1316242720526518');fbq('track','PageView');`;
    document.head.appendChild(script);
  }
  // Microsoft Clarity
  if (!window.clarity) {
    const clarityScript = document.createElement("script");
    clarityScript.innerHTML = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/wfvs98lwr1";y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y)})(window,document,"clarity","script");`;
    document.head.appendChild(clarityScript);
  }
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [advertising, setAdvertising] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setVisible(true);
    } else {
      // Consent exists — load scripts accordingly
      if (consent.analytics) loadAnalytics();
      if (consent.advertising) loadAdvertising();
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { essential: true, analytics: true, advertising: true };
    saveCookieConsent(consent);
    loadAnalytics();
    loadAdvertising();
    setVisible(false);
  };

  const handleRejectNonEssential = () => {
    const consent = { essential: true, analytics: false, advertising: false };
    saveCookieConsent(consent);
    setVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = { essential: true, analytics, advertising };
    saveCookieConsent(consent);
    if (analytics) loadAnalytics();
    if (advertising) loadAdvertising();
    setVisible(false);
    setShowConfig(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4" data-testid="cookie-banner">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl shadow-slate-900/10 border border-slate-200 overflow-hidden">
        {!showConfig ? (
          <div className="p-5">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-9 h-9 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Shield size={18} className="text-orange-500" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800" style={{ fontFamily: "Outfit, sans-serif" }}>Cookie Preferences</h3>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                  We use essential cookies to make the site work. We'd also like to use analytics and advertising cookies to improve our service and show relevant ads. 
                  You can manage your preferences or read our <a href="/privacy" className="text-orange-600 hover:underline">Privacy Policy</a>.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={handleAcceptAll} className="gradient-btn rounded-xl h-9 px-4 text-xs font-semibold">
                Accept All
              </Button>
              <Button onClick={handleRejectNonEssential} variant="outline" className="rounded-xl h-9 px-4 text-xs font-semibold border-slate-200">
                Reject Non-Essential
              </Button>
              <Button onClick={() => setShowConfig(true)} variant="ghost" className="rounded-xl h-9 px-4 text-xs font-semibold text-slate-500">
                Configure
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <h3 className="text-sm font-bold text-slate-800 mb-4" style={{ fontFamily: "Outfit, sans-serif" }}>Cookie Settings</h3>
            <div className="space-y-3 mb-5">
              {/* Essential — always on */}
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                <div>
                  <span className="text-xs font-semibold text-slate-800">Essential Cookies</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">Required for authentication and core functionality</p>
                </div>
                <div className="w-10 h-5 bg-orange-500 rounded-full flex items-center justify-end px-0.5">
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </label>
              {/* Analytics */}
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-xl cursor-pointer" onClick={() => setAnalytics(!analytics)}>
                <div>
                  <span className="text-xs font-semibold text-slate-800">Analytics Cookies</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">Google Analytics — helps us understand how you use the site</p>
                </div>
                <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${analytics ? "bg-orange-500 justify-end" : "bg-slate-300 justify-start"}`}>
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </label>
              {/* Advertising */}
              <label className="flex items-center justify-between p-3 bg-slate-50 rounded-xl cursor-pointer" onClick={() => setAdvertising(!advertising)}>
                <div>
                  <span className="text-xs font-semibold text-slate-800">Advertising Cookies</span>
                  <p className="text-[10px] text-slate-500 mt-0.5">Meta Pixel — used for ad measurement and campaign optimization</p>
                </div>
                <div className={`w-10 h-5 rounded-full flex items-center px-0.5 transition-colors ${advertising ? "bg-orange-500 justify-end" : "bg-slate-300 justify-start"}`}>
                  <div className="w-4 h-4 bg-white rounded-full" />
                </div>
              </label>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleSavePreferences} className="gradient-btn rounded-xl h-9 px-4 text-xs font-semibold">
                Save Preferences
              </Button>
              <Button onClick={() => setShowConfig(false)} variant="ghost" className="rounded-xl h-9 px-4 text-xs text-slate-500">
                Back
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
